'use client'

import {
  AlertCircleIcon,
  CheckCircleIcon,
  LockIcon,
  SendIcon,
} from 'lucide-react'
import { Turnstile } from 'next-turnstile'
import Link from 'next/link'
import { useState } from 'react'
import Toggle from './toggle'

function PrivacyNotice() {
  return (
    <div className="mb-8 rounded-2xl border border-amber-200 bg-gradient-to-r from-amber-50 to-orange-50 p-6 lg:max-w-xs">
      <div className="flex items-start gap-4">
        <div className="flex-shrink-0">
          <LockIcon className="h-6 w-6 text-amber-600" />
        </div>
        <div>
          <h3 className="mb-2 font-bold text-amber-900">
            Your Privacy Matters to Us
          </h3>
          <p className="mb-3 text-sm leading-relaxed text-amber-800">
            We only collect the information you provide in this form to respond
            to your message. We never share, sell, or use your data for
            marketing. Read our full{' '}
            <Link
              href="/privacy"
              className="font-medium underline hover:text-amber-900"
            >
              privacy policy
            </Link>{' '}
            for complete details.
          </p>
          <div className="flex flex-wrap gap-2">
            <span className="rounded bg-amber-100 px-2 py-1 text-xs font-medium text-amber-800">
              No Marketing
            </span>
            <span className="rounded bg-amber-100 px-2 py-1 text-xs font-medium text-amber-800">
              No Data Selling
            </span>
            <span className="rounded bg-amber-100 px-2 py-1 text-xs font-medium text-amber-800">
              Secure Storage
            </span>
          </div>
        </div>
      </div>
    </div>
  )
}

export function ContactForm() {
  const [form, setForm] = useState({
    email: '',
    firstName: '',
    lastName: '',
    subject: '',
    message: '',
    acceptedPrivacy: false,
  })
  let [token, setTurnstileToken] = useState('')

  const [touched, setTouched] = useState({
    email: false,
    message: false,
    acceptedPrivacy: false,
  })

  const [submitted, setSubmitted] = useState(false)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')

  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >,
  ) => {
    const { name, value, type } = e.target
    if (type === 'checkbox') {
      setForm((prev) => ({
        ...prev,
        [name]: (e.target as HTMLInputElement).checked,
      }))
    } else {
      setForm((prev) => ({ ...prev, [name]: value }))
    }
  }

  const handleBlur = (
    e: React.FocusEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >,
  ) => {
    const { name } = e.target
    setTouched((prev) => ({ ...prev, [name]: true }))
  }

  const validate = () => {
    if (!form.email.trim()) return 'Email is required.'
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(form.email))
      return 'Please enter a valid email.'
    if (!form.message.trim()) return 'Message is required.'
    if (!form.acceptedPrivacy) return 'You must accept the privacy policy.'
    return ''
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setTouched({ email: true, message: true, acceptedPrivacy: true })
    const err = validate()
    if (err) {
      setError(err)
      setSuccess('')
      return
    }
    setError('')
    setSubmitted(true)
    try {
      const res = await fetch('/api/message', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ ...form, token }),
      })
      if (!res.ok) {
        throw new Error('Failed to send message. Please try again later.')
      }
      setSuccess(
        "Thank you for reaching out! We've received your message and will respond within 24-48 hours.",
      )
      setForm({
        email: '',
        firstName: '',
        lastName: '',
        subject: '',
        message: '',
        acceptedPrivacy: false,
      })
    } catch (err: any) {
      setError(err.message || 'Something went wrong. Please try again.')
      setSuccess('')
    } finally {
      setSubmitted(false)
    }
  }

  return (
    <div className="flex flex-col items-start justify-between gap-8 lg:flex-row">
      <PrivacyNotice />

      <form
        onSubmit={handleSubmit}
        className="grow rounded-2xl border border-gray-200 bg-white p-8 shadow-lg"
      >
        <div className="mb-6">
          <h3 className="mb-2 text-xl font-bold text-gray-900">
            Send us a message
          </h3>
          <p className="text-gray-600">
            We'll get back to you as soon as possible.
          </p>
        </div>

        <div className="mb-6 grid grid-cols-1 gap-6 md:grid-cols-2">
          <div>
            <label
              htmlFor="firstName"
              className="mb-2 block text-sm font-semibold text-gray-900"
            >
              First Name{' '}
              <span className="text-xs text-gray-700">(optional)</span>
            </label>
            <input
              type="text"
              id="firstName"
              name="firstName"
              autoComplete="given-name"
              className="w-full rounded-lg border border-gray-300 px-4 py-3 text-gray-900 transition-colors focus:border-gray-950 focus:ring-2 focus:ring-gray-200"
              value={form.firstName}
              onChange={handleChange}
              placeholder="Your first name"
            />
          </div>
          <div>
            <label
              htmlFor="lastName"
              className="mb-2 block text-sm font-semibold text-gray-900"
            >
              Last Name{' '}
              <span className="text-xs text-gray-700">(optional)</span>
            </label>
            <input
              type="text"
              id="lastName"
              name="lastName"
              autoComplete="family-name"
              className="w-full rounded-lg border border-gray-300 px-4 py-3 text-gray-900 transition-colors focus:border-gray-950 focus:ring-2 focus:ring-gray-200"
              value={form.lastName}
              onChange={handleChange}
              placeholder="Your last name"
            />
          </div>
        </div>

        <div className="mb-6">
          <label
            htmlFor="email"
            className="mb-2 block text-sm font-semibold text-gray-900"
          >
            Email Address <span className="text-red-500">*</span>
          </label>
          <input
            type="email"
            id="email"
            name="email"
            autoComplete="email"
            required
            className="w-full rounded-lg border border-gray-300 px-4 py-3 text-gray-900 transition-colors focus:border-gray-950 focus:ring-2 focus:ring-gray-200"
            value={form.email}
            onChange={handleChange}
            onBlur={handleBlur}
            placeholder="your.email@example.com"
          />
          {touched.email && !form.email && (
            <div className="mt-2 flex items-center gap-1 text-sm text-red-600">
              <AlertCircleIcon className="h-4 w-4" />
              Email is required.
            </div>
          )}
        </div>

        <div className="mb-6">
          <label
            htmlFor="subject"
            className="mb-2 block text-sm font-semibold text-gray-900"
          >
            Subject <span className="text-xs text-gray-700">(optional)</span>
          </label>
          <select
            id="subject"
            name="subject"
            className="w-full rounded-lg border border-gray-300 px-4 py-3 text-gray-900 transition-colors focus:border-gray-950 focus:ring-2 focus:ring-gray-200"
            value={form.subject}
            onChange={handleChange}
          >
            <option value="">Select a topic</option>
            <option value="general">General Question</option>
            <option value="support">Technical Support</option>
            <option value="education">Educational Content</option>
            <option value="partnership">Partnership/Collaboration</option>
            <option value="feedback">Feedback or Suggestion</option>
            <option value="other">Other</option>
          </select>
        </div>

        <div className="mb-6">
          <label
            htmlFor="message"
            className="mb-2 block text-sm font-semibold text-gray-900"
          >
            Message <span className="text-red-500">*</span>
          </label>
          <textarea
            id="message"
            name="message"
            required
            rows={6}
            className="w-full rounded-lg border border-gray-300 px-4 py-3 text-gray-900 transition-colors focus:border-gray-950 focus:ring-2 focus:ring-gray-200"
            value={form.message}
            onChange={handleChange}
            onBlur={handleBlur}
            placeholder="Tell us how we can help you..."
          />
          {touched.message && !form.message && (
            <div className="mt-2 flex items-center gap-1 text-sm text-red-600">
              <AlertCircleIcon className="h-4 w-4" />
              Message is required.
            </div>
          )}
        </div>

        <div className="mb-6">
          <div className="flex items-start gap-3">
            <Toggle
              id="acceptedPrivacy"
              name="acceptedPrivacy"
              checked={form.acceptedPrivacy}
              onChange={(checked) =>
                setForm((prev) => ({ ...prev, acceptedPrivacy: checked }))
              }
              // className="mt-1"
              aria-required="true"
            />
            <label
              htmlFor="acceptedPrivacy"
              className="cursor-pointer select-none text-sm leading-5 text-gray-700"
            >
              I have read and accept the{' '}
              <Link
                href="/privacy"
                target="_blank"
                className="text-primary-700 hover:text-primary-700 font-medium underline"
              >
                privacy policy
              </Link>{' '}
              and understand how my data will be handled.{' '}
              <span className="text-red-500">*</span>
            </label>
          </div>
          {touched.acceptedPrivacy && !form.acceptedPrivacy && (
            <div className="mt-2 flex items-center gap-1 text-sm text-red-600">
              <AlertCircleIcon className="h-4 w-4" />
              You must accept the privacy policy.
            </div>
          )}
        </div>

        {error && (
          <div className="mb-6 rounded-lg border border-red-200 bg-red-50 p-4">
            <div className="flex items-center gap-2 text-red-800">
              <AlertCircleIcon className="h-5 w-5" />
              <span className="font-medium">{error}</span>
            </div>
          </div>
        )}

        {success && (
          <div className="mb-6 rounded-lg border border-green-200 bg-green-50 p-4">
            <div className="flex items-center gap-2 text-green-800">
              <CheckCircleIcon className="h-5 w-5" />
              <span className="font-medium">{success}</span>
            </div>
          </div>
        )}

        <Turnstile
          theme="light"
          siteKey={process.env.NEXT_PUBLIC_TURNSTILE_SITE_KEY!}
          onError={() => {
            setError('Security check failed. Please refresh the page.')
          }}
          onVerify={(token) => {
            setTurnstileToken(token)
          }}
        />

        <button
          type="submit"
          disabled={submitted}
          className="flex w-full items-center justify-center gap-2 rounded-xl bg-black px-6 py-4 font-semibold text-white transition-all duration-200 hover:bg-gray-800 hover:from-blue-700 hover:to-blue-800 disabled:cursor-not-allowed disabled:from-gray-400 disabled:to-gray-500"
        >
          {submitted ? (
            <>
              <CheckCircleIcon className="h-5 w-5" />
              Message Sent Successfully
            </>
          ) : (
            <>
              <SendIcon className="h-5 w-5" />
              Send Message
            </>
          )}
        </button>
      </form>
    </div>
  )
}
